import React, { useEffect, useState } from "react";
import { MdCreate, MdDelete } from "react-icons/md";
import { getGalleryMainImages, addGalleryMainImages, deleteGalleryMainImages, updateGalleryMainImages } from "Services/allApi"; // Assume these functions exist
import { ToastContainer, toast } from 'react-toastify';
import Swal from 'sweetalert2'; // Import SweetAlert2

function MainImages() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({ index: "", image: null });
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        const fetchImages = async () => {
            const response = await getGalleryMainImages();
            if (response.error) {
                setError(response.message);
            } else {
                setImages(response.data || []);
            }
            setLoading(false);
        };

        fetchImages();
    }, []);

    const openAddModal = () => {
        setIsModalOpen(true);
        setIsEditMode(false);
        setFormData({ index: "", image: null });
        setImagePreview(null);
    };

    const openEditModal = (image) => {
        setIsModalOpen(true);
        setIsEditMode(true);
        setFormData({ index: image.index, image: null, id: image.id });
        setImagePreview(image.image);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSuccessMessage(null); // Reset success message on close
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, index: e.target.value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, image: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleAddOrUpdatePlacementImage = async () => {
        if (!formData.index || !formData.image) {
            toast.error("Please provide index and image");
            return;
        }

        setUploading(true);

        const form = new FormData();
        form.append("index", formData.index);
        form.append("image", formData.image);

        let response;
        if (isEditMode) {
            // Update image
            response = await updateGalleryMainImages(formData.id, form);
        } else {
            // Add new image
            response = await addGalleryMainImages(form);
        }

        setUploading(false);
        if (response.error) {
            setError(response.message);
        } else {
            toast.success(isEditMode ? "Image updated successfully!" : "Image added successfully!");
            setIsModalOpen(false);
            if (isEditMode) {
                // Replace the updated image in the list
                setImages(images.map((image) => image.id === formData.id ? response.data : image));
            } else {
                setImages([response.data, ...images]); // Add new image to the list
            }
        }
    };

    const handleDeletePlacementImage = async (imageId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Do you want to delete this image?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            customClass: {
                popup: 'dark:bg-gray-800 dark:text-white', // Dark background and white text
                title: 'dark:text-white',
                confirmButton: 'dark:bg-blue-500 dark:hover:bg-blue-700 dark:text-white',
                cancelButton: 'dark:bg-red-500 dark:hover:bg-red-700 dark:text-white'
            }
        });

        if (result.isConfirmed) {
            const response = await deleteGalleryMainImages(imageId);
            if (response.error) {
                setError(response.message);
            } else {
                toast.success("Image deleted successfully!");
                setImages(images.filter((image) => image.id !== imageId)); // Remove deleted image from the list
            }
        }
    };

    if (loading) return <p className="text-center text-gray-500">Loading images...</p>;
    if (error) return <p className="text-center text-red-500">Error: {error}</p>;

    return (
        <>
            <div className="mb-4 flex justify-end mt-4">
                <button
                    onClick={openAddModal}
                    className="bg-purple-800 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-purple-700">
                    Add Main Image
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
                {images.length > 0 ? (
                    images.map((image, index) => (
                        <div key={image.id} className="relative">
                            <img
                                src={image.image}
                                alt={`Gallery Image ${index + 1}`}
                                className="w-full h-40 object-cover rounded-md shadow-md"
                            />
                            <div className="absolute top-2 right-2 flex space-x-2">
                                <button
                                    onClick={() => openEditModal(image)}
                                    className="bg-purple-800 text-white px-1 py-1 rounded-lg font-medium text-xs hover:bg-purple-700"
                                >
                                    <MdCreate className="text-lg text-white-700" />
                                </button>
                                <button
                                    onClick={() => handleDeletePlacementImage(image.id)}
                                    className="bg-purple-800 text-white px-1 py-1 rounded-lg font-medium text-xs hover:bg-purple-700"
                                >
                                    <MdDelete className="text-lg text-white-700" />
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="text-center text-gray-500 col-span-full">No images found</p>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50  dark:bg-gray-800 dark:bg-opacity-70 ">
                    <div className="bg-white p-6 rounded-lg max-w-lg w-full dark:bg-[#0b14374d] dark:text-white">
                        <h2 className="text-lg font-semibold mb-4">{isEditMode ? "Edit Image" : "Add Image"}</h2>

                        <label htmlFor="index" className="block font-medium text-gray-900 dark:text-white">Index</label>
                        <input
                            type="text"
                            name="index"
                            id="index"
                            value={formData.index}
                            onChange={handleInputChange}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-purple-800 sm:text-sm mt-2 dark:bg-[#0b14374d] dark:text-white"
                        />

                        <input type="file" accept="image/*" onChange={handleImageChange} className="block w-full mb-5 border rounded-lg cursor-pointer mt-4 dark:bg-[#0b14374d] dark:text-white" />
                        <div className="mb-3">
                            {/* <p className="text-xs text-gray-600">The height and width must be 1375 × 264</p> */}
                        </div>
                        {imagePreview && <img src={imagePreview} alt="Preview" className="h-32 w-32 object-cover rounded-lg" />}

                        <div className="mt-4 flex justify-end">
                            <button onClick={closeModal} className="bg-gray-300 px-4 py-2 rounded-lg mr-2">Cancel</button>
                            <button
                                onClick={handleAddOrUpdatePlacementImage}
                                disabled={uploading}
                                className="bg-purple-800 text-white px-4 py-2 rounded-lg">
                                {uploading ? "Uploading..." : isEditMode ? "Update" : "Add"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-center" autoClose={5000} />
        </>
    );
}

export default MainImages;
